import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Tag } from "@/components/Layout";
import { Sparkles, TrendingUp, TrendingDown } from "lucide-react";
import { CHANNELS, ENGAGEMENT_30D } from "./marketingData";
import SearchConsoleTopPages from "@/components/SearchConsoleTopPages";

function ChannelCard({ ch }) {
  const Icon = ch.Icon;
  const up = ch.growth >= 0;
  return (
    <div className="card-flat p-4" data-testid={`mkt-channel-${ch.id}`}>
      <div className="flex items-center gap-2">
        <span className="w-9 h-9 rounded-sm flex items-center justify-center text-white" style={{ background: ch.color }}>
          <Icon size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-slate-900 flex items-center gap-1">
            {ch.label}
            {ch.isLive && (
              <span className="text-[8px] px-1 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 font-bold" data-testid={`mkt-live-${ch.id}`}>LIVE</span>
            )}
          </div>
          <div className="text-[10px] text-slate-400 truncate">{ch.isLive && ch.handle ? ch.handle : `Posted ${ch.last}`}</div>
        </div>
      </div>
      <div className="mt-3 flex items-end justify-between">
        <div className="font-display text-xl font-bold text-slate-900" data-testid={`mkt-followers-${ch.id}`}>{Number(ch.followers || 0).toLocaleString()}</div>
        {!ch.isLive && (
          <div className={`inline-flex items-center text-[11px] font-semibold ${up ? "text-green-700" : "text-red-700"}`}>
            {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />} {Math.abs(ch.growth).toFixed(1)}%
          </div>
        )}
      </div>
    </div>
  );
}

/** Sparkline component. Accepts either legacy {engagement} or new {value} points. */
export function MiniSpark({ data, color = "#F54800", height = 40 }) {
  if (!data?.length) return null;
  const vals = data.map((d) => Number(d.value ?? d.engagement ?? 0));
  const max = Math.max(1, ...vals);
  const pts = vals.map((v, i) => `${(i / Math.max(1, vals.length - 1)) * 100},${100 - (v / max) * 90 - 5}`).join(" ");
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full" style={{ height }}>
      <polyline fill="none" stroke={color} strokeWidth="2" points={pts} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/** Phase 84.32 — Grouped bar chart: 3 metrics × N channels. Uses SVG so no chart lib is added. */
const METRIC_KEYS = [
  { k: "impressions", l: "Impressions", color: "#F54800" },
  { k: "reach",       l: "Reach",       color: "#0EA5E9" },
  { k: "engagement",  l: "Engagement",  color: "#10B981" },
];
const CHANNEL_COLORS = {
  facebook: "#1877F2",
  instagram: "#E1306C",
  linkedin: "#0A66C2",
  youtube: "#FF0000",
  pinterest: "#E60023",
  gmb: "#34A853",
};
export function ChannelBarChart({ byChannel }) {
  if (!byChannel?.length) {
    return (
      <div className="h-[160px] flex items-center justify-center text-xs text-slate-400" data-testid="mkt-channel-chart-empty">
        No channel-level data yet.
      </div>
    );
  }
  const maxVal = Math.max(1, ...byChannel.flatMap((c) => METRIC_KEYS.map((m) => Number(c[m.k] || 0))));
  const barGroupW = 100 / byChannel.length;
  const barW = (barGroupW * 0.9) / METRIC_KEYS.length; // 3 bars per group, 10% padding
  return (
    <div data-testid="mkt-channel-chart">
      <svg viewBox="0 0 100 60" preserveAspectRatio="none" className="w-full" style={{ height: 200 }}>
        {byChannel.map((c, ci) => (
          <g key={c.id}>
            {METRIC_KEYS.map((m, mi) => {
              const v = Number(c[m.k] || 0);
              const h = (v / maxVal) * 52; // leaves 8u for labels
              const x = ci * barGroupW + barGroupW * 0.05 + mi * barW;
              const y = 55 - h;
              return (
                <rect
                  key={m.k}
                  x={x}
                  y={y}
                  width={barW * 0.92}
                  height={Math.max(0.4, h)}
                  fill={m.color}
                  opacity={0.9}
                  data-testid={`mkt-channel-bar-${c.id}-${m.k}`}
                >
                  <title>{`${c.label} · ${m.l}: ${v.toLocaleString()}`}</title>
                </rect>
              );
            })}
            {/* Channel label */}
            <text
              x={ci * barGroupW + barGroupW / 2}
              y={59}
              textAnchor="middle"
              fontSize="2.2"
              fill={CHANNEL_COLORS[c.id] || "#334155"}
              fontWeight="700"
            >
              {c.label}
            </text>
          </g>
        ))}
      </svg>
      {/* Metric legend */}
      <div className="flex items-center justify-center gap-4 mt-2 flex-wrap">
        {METRIC_KEYS.map((m) => (
          <div key={m.k} className="inline-flex items-center gap-1 text-[10px] text-slate-600" data-testid={`mkt-channel-legend-${m.k}`}>
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: m.color }} />
            {m.l}
          </div>
        ))}
      </div>
      {/* Per-channel numeric strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        {byChannel.map((c) => (
          <div key={c.id} className="bg-slate-50 rounded-sm p-2 text-[10px]" data-testid={`mkt-channel-numbers-${c.id}`}>
            <div className="font-bold text-slate-900 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: CHANNEL_COLORS[c.id] || "#334155" }} />
              {c.label}
            </div>
            {METRIC_KEYS.map((m) => (
              <div key={m.k} className="flex justify-between text-slate-600 mt-0.5">
                <span>{m.l}</span>
                <span className="font-mono text-slate-900">{Number(c[m.k] || 0).toLocaleString()}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Compact formatter for KPI counts (12,432 → 12.4k). */
function nf(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 10_000) return `${(v / 1000).toFixed(1)}k`;
  if (v >= 1_000) return `${(v / 1000).toFixed(2)}k`;
  return String(v);
}

/** Static directional tiles used until Meta clears App Review for that specific metric. */
const FALLBACK_TILES = [
  { key: "click_throughs", l: "Click-throughs", v: "1,820", d: +18.4, live: false },
  { key: "inbound_dms",    l: "Inbound DMs",    v: "42",    d: +24.0, live: false },
];

const INSIGHTS = [
  { sev: "high",   t: "LinkedIn is your unfair advantage", b: "Your LinkedIn posts get 3.2x the engagement of every other channel. You're publishing only 1x/week. Move to 3x/week — your top-performing post (14.8k views) was published on a Tuesday at 9 AM IST. Stick to that slot." },
  { sev: "medium", t: "Pinterest is the only channel slipping (-1.2%)", b: "Pinterest is mostly auto-reshares with no fresh pins in 28 days. Either commit (one pin a day, 7 days) or sunset and reinvest the hour elsewhere." },
  { sev: "high",   t: "Carousel beats reel for you 2:1 on save-rate", b: "Reels get views, but carousels get bookmarks. For a B2B founder audience, bookmarks → leads. Shift ratio to 70% carousel / 30% reel and watch DMs climb." },
  { sev: "low",    t: "You have ZERO scheduled posts for next week", b: "Use the composer + AI hook library to queue 5 posts before Friday. The Calendar tab is the easiest way." },
];

export default function OverviewSection({ liveStats }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartView, setChartView] = useState("combined"); // "combined" | "by_channel"
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await api.get("/marketing/insights?days=30");
        if (live) setInsights(data);
      } catch { /* fall back to placeholders */ }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, []);

  // Merge live Meta stats into channel cards. Anything unconnected shows the
  // "not connected" state rather than fake numbers.
  const merged = CHANNELS.map((ch) => {
    const live = (liveStats?.channels || []).find((c) => c.id === ch.id);
    if (live && live.connected && live.followers != null) {
      return { ...ch, followers: live.followers, handle: live.handle, connected: true, isLive: true };
    }
    if (live) return { ...ch, connected: !!live.connected, isLive: false, handle: live.handle };
    return { ...ch, isLive: false };
  });

  // Build the live KPI tiles from /marketing/insights. Fall back to seeded
  // benchmarks for anything Meta doesn't expose without App Review.
  const kpis = insights?.kpis || {};
  const tiles = useMemo(() => ([
    { key: "impressions",   l: "Impressions",   v: nf(kpis.impressions?.value),   d: kpis.impressions?.delta_pct ?? 0,   live: !!kpis.impressions },
    { key: "reach",         l: "Reach",         v: nf(kpis.reach?.value),         d: kpis.reach?.delta_pct ?? 0,         live: !!kpis.reach },
    { key: "engagement",    l: "Engagement",    v: nf(kpis.engagement?.value),    d: kpis.engagement?.delta_pct ?? 0,    live: !!kpis.engagement },
    { key: "profile_views", l: "Profile visits",v: nf(kpis.profile_views?.value), d: kpis.profile_views?.delta_pct ?? 0, live: !!kpis.profile_views },
    ...FALLBACK_TILES,
  ]), [kpis]);

  const chartData = insights?.series?.length ? insights.series : ENGAGEMENT_30D;

  return (
    <div data-testid="mkt-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {merged.map((ch) => <ChannelCard key={ch.id} ch={ch} />)}
      </div>

      <div className="card-flat p-5 mb-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="label-overline">Engagement — last 30 days</div>
            <div className="font-display text-lg font-bold text-slate-900 mt-0.5 flex items-center gap-2">
              {chartView === "combined" ? "All channels combined" : "Breakdown by channel"}
              {insights?.ok && (
                <span className="text-[8px] px-1 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 font-bold" data-testid="mkt-kpis-live">LIVE</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Chart-view tabs */}
            <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden" data-testid="mkt-chart-view-tabs">
              <button
                onClick={() => setChartView("combined")}
                className={`px-3 py-1 text-[11px] font-semibold ${chartView === "combined" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                data-testid="mkt-chart-view-combined"
              >Combined</button>
              <button
                onClick={() => setChartView("by_channel")}
                className={`px-3 py-1 text-[11px] font-semibold ${chartView === "by_channel" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                data-testid="mkt-chart-view-by-channel"
              >By channel</button>
            </div>
            <Sparkles size={14} className="text-[#F54800]" />
          </div>
        </div>
        {chartView === "combined" ? (
          <MiniSpark data={chartData} height={120} />
        ) : (
          <ChannelBarChart byChannel={insights?.by_channel || []} />
        )}
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mt-4">
          {tiles.map((k) => (
            <div key={k.key} className="bg-slate-50 rounded-sm p-2.5" data-testid={`mkt-kpi-${k.key}`}>
              <div className="label-overline flex items-center gap-1">
                {k.l}
                {k.live && (
                  <span className="text-[7px] px-1 py-0 rounded-sm bg-emerald-100 text-emerald-700 font-bold" title="Live from Meta Graph API">LIVE</span>
                )}
              </div>
              <div className="font-display text-base font-bold text-slate-900 mt-1">
                {loading && !k.live ? "—" : k.v}
              </div>
              <div className={`text-[10px] font-semibold ${k.d >= 0 ? "text-green-700" : "text-red-700"}`}>
                {k.d >= 0 ? "+" : ""}{Number(k.d || 0).toFixed(1)}%
              </div>
            </div>
          ))}
        </div>
        <div className="text-[10px] text-slate-400 mt-3 italic">
          Follower counts + Impressions / Reach / Engagement / Profile visits pull live from Meta Graph API v21 (last 30 days vs prior 30). Click-throughs and Inbound DMs remain seeded benchmarks — those require App Review.
        </div>
      </div>

      <div className="card-flat p-5 mb-6" data-testid="mkt-ai-insights">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-7 h-7 rounded-sm bg-[#F54800] text-white flex items-center justify-center"><Sparkles size={13} /></span>
          <div>
            <div className="label-overline">AI strategist</div>
            <p className="text-[11px] text-slate-500 mt-0.5">Auto-analysis of your last 30 days vs your niche benchmarks.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {INSIGHTS.map((ins, i) => {
            const tone = ins.sev === "high" ? "border-l-red-500 bg-red-50/40" : ins.sev === "medium" ? "border-l-amber-400 bg-amber-50/40" : "border-l-blue-400 bg-blue-50/40";
            return (
              <div key={ins.t} className={`border-l-4 ${tone} p-3 rounded-sm`} data-testid={`mkt-insight-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display font-bold text-slate-900 text-sm">{ins.t}</div>
                  <Tag tone={ins.sev === "high" ? "red" : ins.sev === "medium" ? "yellow" : "blue"}>{ins.sev}</Tag>
                </div>
                <p className="text-xs text-slate-700 mt-1 leading-relaxed">{ins.b}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Phase 84.36 — Search Console top pages. LIVE via the existing
          Google OAuth scope (webmasters.readonly). Zero new credentials. */}
      <SearchConsoleTopPages />
    </div>
  );
}
